import { BillingFacturationNetResponse } from "@/Billing/Api/FacturationNet/Dto/Responses/FacNetBillLightResponse";
import { ExternalBillingClaimDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimDto";
import { AttachedCommunication } from "@/Common/Models/AttachedCommunication/AttachedCommunication";
import { Guid } from "@/Common/Models/Guid";
import { LightPrivateBill } from "@/Patient/Api/PrivateBill/Dto/LightPrivateBill";
import { SentFaxInfo } from "@/Patient/Models/Fax/SentFaxInfo";
import { PatientForms } from "@/Patient/Models/Forms/RadPdf/Forms";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { EncounterNote } from "@/Patient/Models/MedicalSummary/EncounterNote";
import { MedicalItemAlertsAndSpecialNeeds } from "@/Patient/Models/MedicalSummary/MedicalItemAlertsAndAllergies";
import { MedicalItemFamilyHistory } from "@/Patient/Models/MedicalSummary/MedicalItemFamilyHistory";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemMedicalReaction } from "@/Patient/Models/MedicalSummary/MedicalItemMedicalReaction";
import { MedicalItemMedicationAndPrescription } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescription";
import { MedicalItemProblemAndCondition } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { MedicalItemScreeningTestResponse } from "@/Patient/Models/MedicalSummary/Response/MedicalItemScreeningTestResponse";
import { PatientPrescriptionItem } from "@/Patient/Models/Medication/PatientPrescriptionItem";
import { BaseObstetricalFileModel } from "@/Patient/Models/ObstetricalFile/BaseObstetricalFileModel";
import { ObstetricalFileModel } from "@/Patient/Models/ObstetricalFile/ObstetricalFileModel";
import { ConsultationRequestsInboxItem, RequestsInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { FreeFormImage } from "./FreeFormImage";
import { PatientFormAPSS } from "./PatientFormAPSS";

class MedicalNoteModel {
    public PatientContactId: string;
    public PatientInactivableId: string;
    public EncounterIsDeletable: boolean;
    public EncounterUnapprouvability: EncounterUnapprouvability;
    public Encounter: Encounter;
    public EncounterNotes: EncounterNote[];
    public MedicalItemsAlertAndSpecialNeed: MedicalItemAlertsAndSpecialNeeds[];
    public MedicalItemsFamilyHistory: MedicalItemFamilyHistory[];
    public MedicalItemsImmunization: MedicalItemImmunization[];
    public MedicalItemsMedicalReaction: MedicalItemMedicalReaction[];
    public MedicalItemsMedicationAndPrescription: MedicalItemMedicationAndPrescription[];
    public MedicalItemsProblemsAndDiagnostics: MedicalItemProblemAndCondition[];
    public MedicalItemsRiskFactor: MedicalItemRiskFactor[];
    public MedicalItemsVitalSign: MedicalItemVitalSign[];
    public MedicalItemsScreeningTest: MedicalItemScreeningTestResponse[];

    public Prescriptions: PatientPrescriptionItem[];
    public PrivateBills: LightPrivateBill[];
    public Requests: RequestsInboxItem[];
    public ConsultationRequests: ConsultationRequestsInboxItem[];
    public HandwrittenNotes: FreeFormImage[];
    public PatientForms: PatientForms[];
    public PatientAPSSForms: PatientFormAPSS[];
    public FacturationNetBills: BillingFacturationNetResponse[];
    public ExternalBillingClaims: ExternalBillingClaimDto[];
    public SentFaxes: { [key: Guid]: SentFaxInfo[] };

    public IndividualTaskLinkedItems: AttachedCommunication[];
    public AreAllTasksCompleted: boolean;
    public AreAllMessagesRead: boolean;
    public IndividualMessageLinkedItems: AttachedCommunication[];
    public IndividualRecallLinkedItems: AttachedCommunication[];
    public AreAllRecallsCompleted: boolean;

    public ObstetricalFileModel: ObstetricalFileModel;
    public PreviousObstetricalFiles: BaseObstetricalFileModel[];
    public HasPreviousObstetricalPdfForms: boolean;
    public HasObstetricalFilesInOtherEncounters: boolean;
}

enum NoteLockedReason {
    ItIsNotYourNoteAndYouAreNotAnAdmin,
    NoteIsTooOldAndYouAreNotAnAdmin,
    NoteWasSignedInAnotherSystem,
    OnlyTheMostRecentNoteMayBeUnApprouved,
    SomeSummaryItemsAddedInThisNoteMayNotBeModifiedAsTheyAreNowPartOfMoreRecentNotes,
    NoteHasBeenPrintedOrFaxed
}

class EncounterUnapprouvability {
    public CanUnapprouve: boolean;
    public NoteLockedReasons: NoteLockedReason[];

    public static DisplayableReasons: NoteLockedReason[] = [
        NoteLockedReason.SomeSummaryItemsAddedInThisNoteMayNotBeModifiedAsTheyAreNowPartOfMoreRecentNotes,
        NoteLockedReason.NoteHasBeenPrintedOrFaxed
    ];

    public static ShouldDisplayReason(noteLockedReason: NoteLockedReason[]): boolean {
        return noteLockedReason.length > 0 && noteLockedReason.every(x => EncounterUnapprouvability.DisplayableReasons.contains(x)); // Only display if those are the only reasons
    }

    public static GetReasonString(noteLockedReason: NoteLockedReason): string {
        switch (noteLockedReason) {
            case NoteLockedReason.ItIsNotYourNoteAndYouAreNotAnAdmin:
                return cacheStore.GetString("NotYourNoteAndYouAreNotAnAdminLabel");
            case NoteLockedReason.NoteIsTooOldAndYouAreNotAnAdmin:
                return cacheStore.GetString("NoteIsTooOldAndYouAreNotAnAdminLabel");
            case NoteLockedReason.NoteWasSignedInAnotherSystem:
                return cacheStore.GetString("NoteWasSignedInAnotherSystemLabel");
            case NoteLockedReason.OnlyTheMostRecentNoteMayBeUnApprouved:
                return cacheStore.GetString("OnlyMostRecentNoteMayBeUnApprouvedLabel");
            case NoteLockedReason.SomeSummaryItemsAddedInThisNoteMayNotBeModifiedAsTheyAreNowPartOfMoreRecentNotes:
                return cacheStore.GetString("SomeSummaryElementsWereModifiedInAMoreRecentNoteLabel");
            case NoteLockedReason.NoteHasBeenPrintedOrFaxed:
                return cacheStore.GetString("NoteHasBeenPrintedOrFaxedLabel");
            default:
                return NoteLockedReason[noteLockedReason].toString();
        }
    }
}

export { EncounterUnapprouvability, MedicalNoteModel, NoteLockedReason };
